import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useSelectedLocation } from '../hooks/useSelectedLocation';
import { useUser } from '../context/UserContext';
import { useAppState } from '../hooks/useAppState';
import { useOrganizations, useOrganizationLocations } from '../hooks/useOrganizationData';


const Sidebar = ({navItems, userOrganizationId }) => {
    const { logout } = useAuth0();
    const { t, i18n } = useTranslation();
    const { isAdmin, isSuperAdmin, canManageProducts } = useUser();

    // Get country from centralized state to trigger language change
    const { country } = useAppState();

    // Update language when country changes
    useEffect(() => {
        const countryLanguageMap = {
            US: 'US-EN',
            USA: 'US-EN',
            Canada: 'CA-EN',
        };
        const language = countryLanguageMap[country];
        if (language && i18n.language !== language) {
            i18n.changeLanguage(language);
        }
    }, [country, i18n]);
    const location = useLocation();

    const [selectedOrganization, setSelectedOrganization] = useState(() => {
        return localStorage.getItem('selectedOrganization') || userOrganizationId || '';
    });
    const selectedLocation = useSelectedLocation();

    const isSuper = isSuperAdmin();

    // All organizations (super_admin only). React Query dedupes + caches, so this
    // is safe to call on every render regardless of how often deps churn.
    const { data: organizations = [] } = useOrganizations({ enabled: isSuper });

    // Locations for the active org. Use localStorage org as fallback so locations
    // load even before Redux is populated. The hook is keyed on orgToUse, so it
    // refetches only when the org actually changes.
    const orgToUse = isSuper ? selectedOrganization : (userOrganizationId || selectedOrganization);
    const { data: locations = [] } = useOrganizationLocations(orgToUse);

    // Set default selected organization when userOrganizationId is available
    useEffect(() => {
        if (userOrganizationId && !localStorage.getItem('selectedOrganization')) {
            setSelectedOrganization(userOrganizationId);
            localStorage.setItem('selectedOrganization', userOrganizationId);
        }
    }, [userOrganizationId]);

    // Once locations load, pick a default store if none is selected (or the
    // selected one doesn't belong to the current org). This runs off the cached
    // query data, so its reference is stable and it won't loop.
    useEffect(() => {
        if (!locations.length) return;
        const currentLocation = localStorage.getItem('selectedLocation');
        // MongoDB returns _id, not id
        const locationBelongsToOrg = locations.some(loc => (loc._id || loc.id)?.toString() === currentLocation);
        if (!currentLocation || !locationBelongsToOrg) {
            localStorage.setItem('selectedLocation', locations[0]._id || locations[0].id);
            // Dispatch custom event for same-tab listeners (native 'storage' only works cross-tab)
            window.dispatchEvent(new Event('storageEvent'));
        }
    }, [locations]);

    const handleOrganizationChange = (event) => {
        const newOrg = event.target.value;
        setSelectedOrganization(newOrg);
        localStorage.setItem('selectedOrganization', newOrg);
        // Clear selected location when org changes - a default is chosen once the new org's locations load
        localStorage.removeItem('selectedLocation');
    };

    const handleLocationChange = (event) => {
        const newLocation = event.target.value;
        localStorage.setItem('selectedLocation', newLocation);
        // Dispatch custom event for same-tab listeners (native 'storage' only works cross-tab)
        window.dispatchEvent(new Event('storageEvent'));
    };

    return (
        <div className="sidebar">
            <div id="side_logo" className="sidebar-nav-div">
                <div className="sidebar-nav-logo">
                    <img src="/adgro-logo-color.png"  sizes="(max-width: 767px) 10px, 1vw"  alt="" className="sidebar-nav-logo-image"></img>
                </div>        
            </div>
            <div id="top_side_nav" className="sidebar-nav-div">
                <ul className="sidebar-nav-list sidebar-nav-list-unstyled">
                    {navItems === "DASHBOARD" && (
                        <>
                            <li id="dashboard-link" className="sidebar-nav-list-item"><Link to="/dashboard" className="sidebar-nav-list-item">{t('dashboard-nav.main-nav.dashboard')}</Link></li>
                            {/* Only show Products link if user can manage products (admin/super_admin) */}
                            {canManageProducts() && (
                                <li id="product-catalog-link" className="sidebar-nav-list-item sidebar-nav-list-item-expandable"><Link to="/products" className="sidebar-nav-list-item">{t('dashboard-nav.main-nav.product-catalog')}</Link></li>
                            )}
                            <li id="flyers-link" className="sidebar-nav-list-item sidebar-nav-list-item-expandable"><Link to="/flyers" className="sidebar-nav-list-item">{t('dashboard-nav.main-nav.flyers')}</Link></li>
                            {isAdmin() && (
                                <li id="website-link" className="sidebar-nav-list-item sidebar-nav-list-item-expandable"><Link to="/website/pages" className="sidebar-nav-list-item">Website</Link></li>
                            )}
                            {isAdmin() && (
                                <>
                                    <li className="sidebar-nav-list-item" style={{ height: '10px' }}>&nbsp;</li>
                                    <li id="settings-link" className="sidebar-nav-list-item sidebar-nav-list-item-expandable"><Link to="/settings/organization" className="sidebar-nav-list-item">{t('dashboard-nav.main-nav.settings')}</Link></li>
                                </>
                            )}
                            {isSuperAdmin() && (
                                <>
                                    <li className="sidebar-nav-list-item" style={{ height: '10px' }}>&nbsp;</li>
                                    <li id="admin-link" className="sidebar-nav-list-item sidebar-nav-list-item-expandable"><Link to="/settings/organizations" className="sidebar-nav-list-item">Admin</Link></li>
                                </>
                            )}
                        </>
                    )}
                    {navItems === "PRODUCTS" && canManageProducts() && (
                        <>
                            <li id="dashboard-link" className="sidebar-nav-list-item"><Link to="/dashboard" className="sidebar-nav-list-item">❮&nbsp;{t('dashboard-nav.main-nav.back-to')}</Link></li>
                            <li className="sidebar-nav-list-item">&nbsp;</li>
                            <li id="product-catalog-link" className="sidebar-nav-list-item"><Link to="/products" className="sidebar-nav-list-item">{t('dashboard-nav.products-nav.overview')}</Link></li>
                            <li id="product-add-new-link" className="sidebar-nav-list-item"><Link to="/addproduct" className="sidebar-nav-list-item">{t('dashboard-nav.products-nav.new')}</Link></li>
                            <li id="product-bulk-upload-link" className="sidebar-nav-list-item"><Link to="/bulkuploadproducts" className="sidebar-nav-list-item">+ {t('dashboard-nav.products-nav.upload')}</Link></li>
                        </>
                    )}
                    {navItems === "FLYERS" && (
                        <>
                            <li id="flyers-link" className="sidebar-nav-list-item"><Link to="/dashboard" className="sidebar-nav-list-item">❮&nbsp;{t('dashboard-nav.main-nav.back-to')}</Link></li>
                            <li className="sidebar-nav-list-item">&nbsp;</li>
                            <li id="flyers-link" className="sidebar-nav-list-item"><Link to="/flyers" className="sidebar-nav-list-item">{t('dashboard-nav.flyers-nav.overview')}</Link></li>
                            <li id="flyers-add-new-link" className="sidebar-nav-list-item"><Link to="/addflyer" className="sidebar-nav-list-item">{t('dashboard-nav.flyers-nav.new-flyer')}</Link></li>
                            {/* Only show template management for admins - agents can only use existing templates */}
                            <li id="flyers-templates-link" className="sidebar-nav-list-item"><Link to="/flyertemplates" className="sidebar-nav-list-item">{t('dashboard-nav.flyers-nav.flyer-templates')}</Link></li>
                            {isAdmin() && (
                                <li id="flyers-add-new-flyertemplate-link" className="sidebar-nav-list-item"><Link to="/flyertemplatemanager" className="sidebar-nav-list-item">{t('dashboard-nav.flyers-nav.new-flyer-template')}</Link></li>
                            )}
                            <li id="flyers-ad-templates-link" className="sidebar-nav-list-item"><Link to="/adtemplates" className="sidebar-nav-list-item">{t('dashboard-nav.flyers-nav.ad-templates')}</Link></li>
                            {isAdmin() && (
                                <li id="flyers-add-new-product-ad-template-link" className="sidebar-nav-list-item"><Link to="/adtemplatemanager" className="sidebar-nav-list-item">{t('dashboard-nav.flyers-nav.new-ad-template')}</Link></li>
                            )}
                        </>
                    )}
                    {navItems === "WEBSITE" && (
                        <>
                            <li id="dashboard-link" className="sidebar-nav-list-item"><Link to="/dashboard" className="sidebar-nav-list-item">❮&nbsp;{t('dashboard-nav.main-nav.back-to')}</Link></li>
                            <li className="sidebar-nav-list-item">&nbsp;</li>
                            <li id="website-pages-link" className={`sidebar-nav-list-item${location.pathname === '/website/pages' ? ' active' : ''}`}>
                                <Link to="/website/pages" className="sidebar-nav-list-item">Pages</Link>
                            </li>
                            <li id="website-seo-link" className={`sidebar-nav-list-item${location.pathname === '/website/seo' ? ' active' : ''}`}>
                                <Link to="/website/seo" className="sidebar-nav-list-item">SEO</Link>
                            </li>
                            <li id="website-theme-link" className={`sidebar-nav-list-item${location.pathname === '/website/theme' ? ' active' : ''}`}>
                                <Link to="/website/theme" className="sidebar-nav-list-item">Theme & Branding</Link>
                            </li>
                            <li id="website-social-link" className={`sidebar-nav-list-item${location.pathname === '/website/social' ? ' active' : ''}`}>
                                <Link to="/website/social" className="sidebar-nav-list-item">Social Links</Link>
                            </li>
                            <li id="website-domain-link" className={`sidebar-nav-list-item${location.pathname === '/website/domain' ? ' active' : ''}`}>
                                <Link to="/website/domain" className="sidebar-nav-list-item">Domain</Link>
                            </li>
                        </>
                    )}
                    {navItems === "SETTINGS" && (
                        <>
                            <li id="dashboard-link" className="sidebar-nav-list-item"><Link to="/dashboard" className="sidebar-nav-list-item">❮&nbsp;{t('dashboard-nav.main-nav.back-to')}</Link></li>
                            <li className="sidebar-nav-list-item">&nbsp;</li>
                            <li id="settings-org-link" className={`sidebar-nav-list-item${location.pathname.startsWith('/settings/organization') && !location.pathname.startsWith('/settings/organizations') ? ' active' : ''}`}>
                                <Link to="/settings/organization" className="sidebar-nav-list-item">Organization</Link>
                            </li>
                            <li id="settings-locations-link" className={`sidebar-nav-list-item${location.pathname.startsWith('/settings/locations') ? ' active' : ''}`}>
                                <Link to="/settings/locations" className="sidebar-nav-list-item">Locations</Link>
                            </li>
                            <li id="settings-users-link" className={`sidebar-nav-list-item${location.pathname.startsWith('/settings/users') ? ' active' : ''}`}>
                                <Link to="/settings/users" className="sidebar-nav-list-item">Users</Link>
                            </li>
                        </>
                    )}
                    {navItems === "ADMIN" && (
                        <>
                            <li id="dashboard-link" className="sidebar-nav-list-item"><Link to="/dashboard" className="sidebar-nav-list-item">❮&nbsp;{t('dashboard-nav.main-nav.back-to')}</Link></li>
                            <li className="sidebar-nav-list-item">&nbsp;</li>
                            <li id="admin-orgs-link" className={`sidebar-nav-list-item${location.pathname === '/settings/organizations' || location.pathname.startsWith('/settings/organizations/') ? ' active' : ''}`}>
                                <Link to="/settings/organizations" className="sidebar-nav-list-item">Organizations</Link>
                            </li>
                            <li id="admin-product-library-link" className={`sidebar-nav-list-item${location.pathname === '/settings/universal-products' ? ' active' : ''}`}>
                                <Link to="/settings/universal-products" className="sidebar-nav-list-item">Product Library</Link>
                            </li>
                            <li id="admin-flyer-analysis-link" className={`sidebar-nav-list-item${location.pathname.startsWith('/flyer-analysis') ? ' active' : ''}`}>
                                <Link to="/flyer-analysis" className="sidebar-nav-list-item">Flyer Analysis</Link>
                            </li>
                        </>
                    )}
                </ul>
            </div>
            <div id="mid_side_nav" className="sidebar-nav-div">
                <ul className="sidebar-nav-list sidebar-nav-list-unstyled">
                    {isSuperAdmin() && (
                        <li id="org-switcher" className="sidebar-nav-list-item">
                            <Select
                                className="menu-dropdown sidebar-nav-list-dropdown"
                                value={selectedOrganization}
                                onChange={handleOrganizationChange}
                                displayEmpty
                            >
                                <MenuItem value="" disabled>Select Organization</MenuItem>
                                {organizations.map((org) => (
                                    <MenuItem className="sidebar-nav-list-dropdown-menuitem" key={org.id} value={org.id}>
                                        {org.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </li>
                    )}
                    {isSuperAdmin() && <li className="sidebar-nav-list-item" style={{ height: '10px' }}>&nbsp;</li>}
                    <li id="store-switcher" className="sidebar-nav-list-item">
                        <Select
                            className="menu-dropdown sidebar-nav-list-dropdown"
                            value={selectedLocation}
                            onChange={handleLocationChange}
                            displayEmpty
                            >
                            <MenuItem value="" disabled>Select Store</MenuItem>
                            {locations.map((loc) => (
                                <MenuItem className="sidebar-nav-list-dropdown-menuitem" key={loc._id || loc.id} value={loc._id || loc.id}>
                                    {loc.store_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </li>
                    <li className="sidebar-nav-list-item">&nbsp;</li>
                    <li id="logout-link" className="sidebar-nav-list-item"><span className="sidebar-nav-list-item" style={{ cursor: 'pointer' }} onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>{t('dashboard-nav.main-nav.logout')}</span></li>
                </ul>
            </div>
            <div id="bottom_side_nav" className="sidebar-nav-div">
                <ul className="sidebar-nav-list sidebar-nav-list-unstyled">
                    <li id="terms-nav-link" className="sidebar-nav-footer-list-item"><Link to="/terms" className="sidebar-nav-footer-list-item">{t('footer.terms')}</Link></li>
                    <li id="privacy-nav-link" className="sidebar-nav-footer-list-item"><Link to="/privacy" className="sidebar-nav-footer-list-item">{t('footer.privacy')}</Link></li>
                </ul>
            </div>
            <div className="sidebar-nav-div">
                <div className="sidebar-nav-text-block">
                    <span>{t('footer.copyright')}</span>
                </div>
            </div>
        </div>
    );
}

export default Sidebar;